import { useMemo } from 'react';
import { Eye } from 'lucide-react';
import { cn } from '../../utils/helpers';
import {
  buildPreviewVariables,
  renderEmailTemplateString,
} from '../../utils/emailPreview';

type EmailTemplatePreviewProps = {
  subject: string;
  htmlBody: string;
  variableNames: string[];
  /** Override sample values (merged on top of defaults). */
  previewVariables?: Record<string, string>;
  compact?: boolean;
  /** Taller iframe for sheet / modal layouts. */
  tall?: boolean;
};

export function EmailTemplatePreview({
  subject,
  htmlBody,
  variableNames,
  previewVariables = {},
  compact = false,
  tall = false,
}: EmailTemplatePreviewProps) {
  const variables = useMemo(() => {
    const base = buildPreviewVariables(variableNames);
    return { ...base, ...previewVariables };
  }, [variableNames, previewVariables]);

  const renderedSubject = useMemo(
    () => renderEmailTemplateString(subject || '(no subject)', variables),
    [subject, variables]
  );

  const renderedHtml = useMemo(
    () =>
      renderEmailTemplateString(
        htmlBody || '<p style="color:#666;font-family:sans-serif;padding:16px;">No HTML body yet.</p>',
        variables
      ),
    [htmlBody, variables]
  );

  return (
    <div className={compact ? 'space-y-3' : 'space-y-4'}>
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#64748b]">
        <Eye className="w-3.5 h-3.5" />
        Live preview
      </div>

      <div className="rounded-xl border border-[#252535] bg-[#0f0f1a] overflow-hidden">
        <div className="px-4 py-3 border-b border-[#252535] bg-[#141420]">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#64748b] mb-1">
            Subject
          </p>
          <p className="text-sm text-[#f1f5f9] break-words">{renderedSubject}</p>
        </div>

        <div className="bg-[#e8e8e8] p-3">
          <iframe
            title="Email HTML preview"
            sandbox=""
            srcDoc={renderedHtml}
            className={cn(
              'w-full rounded-lg border border-[#d4d4d4] bg-white shadow-sm',
              compact ? 'h-[280px]' : tall ? 'h-[min(520px,55vh)]' : 'h-[360px]'
            )}
          />
        </div>
      </div>

      {variableNames.length > 0 && (
        <div className="rounded-lg border border-[#252535] bg-[#10101a] px-3 py-2">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#64748b] mb-1.5">
            Preview values
          </p>
          <div className="flex flex-wrap gap-1.5">
            {variableNames.map((name) => (
              <span
                key={name}
                className="inline-flex items-center gap-1 rounded-md bg-[#1a2235] px-2 py-0.5 text-[11px] font-mono text-[#94a3b8]"
              >
                <span className="text-[#8fb4ff]">{name}</span>
                <span className="text-[#64748b]">=</span>
                <span className="text-[#e2e8f0]">{variables[name] ?? '-'}</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
